import type { OutgoingAttachment } from 'zitimail-shared-types';

/**
 * Pictures in the body of a message being written (a pasted screenshot, a
 * dropped photo, a quoted original's). The editor shows each by a preview
 * address of this page (`blob:`); the message carries the bytes as an inline
 * part, and its HTML names it `cid:<contentId>` (multipart/related). Preview
 * addresses never leave this page: `outgoing` swaps them for Content-IDs.
 */
export interface InlineImage extends OutgoingAttachment {
  readonly contentId: string;
  readonly size: number;
  /** Where the editor shows it; never sent. */
  readonly url?: string;
}

/** The pictures every mail app shows, and the only ones the reader shows. */
export const PICTURE_TYPES = /^image\/(png|jpeg|gif|webp)$/;

const DATA_URL = /^data:(image\/(?:png|jpeg|gif|webp));base64,([A-Za-z0-9+/=\s]+)$/i;

const EXTENSION: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

export function newContentId(): string {
  return `${crypto.randomUUID()}@zitimail`;
}

function bytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/** The pictures of one composer, by preview address. */
export class Pictures {
  private readonly byUrl = new Map<string, InlineImage>();

  /** A picture to show: its preview address is made from `blob` or its bytes. */
  add(picture: Omit<InlineImage, 'url'>, blob?: Blob): InlineImage {
    const url = URL.createObjectURL(
      blob ?? new Blob([bytes(picture.data)], { type: picture.contentType }),
    );
    const shown = { ...picture, url };
    this.byUrl.set(url, shown);
    return shown;
  }

  /**
   * HTML for the editor: a picture given as `data:` (a quoted original's, a
   * reopened draft's) gets a preview address; one with no address is a
   * reopened draft's too big for the reader to inline, matched to its part
   * by name, then by order. `known`: the pictures that came with the draft.
   */
  localise(html: string, known: readonly InlineImage[] = []): string {
    if (!/<img\b/i.test(html)) {
      return html;
    }
    const waiting = known.filter((k) => {
      if (k.url && this.byUrl.has(k.url)) {
        return false;
      }
      if (k.url) {
        // Reopened after Undo: the preview this page made is still there.
        this.byUrl.set(k.url, k);
        return false;
      }
      return true;
    });
    const take = (match: (k: InlineImage) => boolean): InlineImage | undefined => {
      const i = waiting.findIndex(match);
      return i < 0 ? undefined : waiting.splice(i, 1)[0];
    };
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // The same picture shown twice is one part, as it was in the original.
    const made = new Map<string, string>();
    for (const img of Array.from(doc.querySelectorAll('img'))) {
      const src = (img.getAttribute('src') ?? '').trim();
      if (this.byUrl.has(src) || /^https:/i.test(src)) {
        continue;
      }
      const data = DATA_URL.exec(src);
      if (data) {
        const base64 = (data[2] ?? '').replace(/\s/g, '');
        const type = (data[1] ?? 'image/png').toLowerCase();
        const url =
          made.get(base64) ??
          this.add(
            take((k) => k.data === base64) ?? {
              filename:
                img.getAttribute('alt')?.trim() ||
                `image-${made.size + 1}.${EXTENSION[type] ?? 'png'}`,
              contentType: type,
              data: base64,
              size: Math.floor((base64.length * 3) / 4),
              contentId: newContentId(),
            },
          ).url ??
          '';
        made.set(base64, url);
        img.setAttribute('src', url);
        continue;
      }
      const alt = img.getAttribute('alt')?.trim();
      const draft = !src ? (take((k) => k.filename === alt) ?? take(() => true)) : undefined;
      if (draft) {
        img.setAttribute('src', this.add(draft).url ?? '');
        if (!alt) {
          img.setAttribute('alt', draft.filename);
        }
      } else {
        // Nothing to show: a blocked or broken picture, or a stale preview.
        img.remove();
      }
    }
    return doc.body.innerHTML;
  }

  /** The pictures `html` shows, each once. */
  shown(html: string): InlineImage[] {
    const seen = new Map<string, InlineImage>();
    for (const m of html.matchAll(/<img\b[^>]*\bsrc="([^"]*)"/gi)) {
      const picture = this.byUrl.get(m[1] ?? '');
      if (picture) {
        seen.set(picture.contentId, picture);
      }
    }
    return [...seen.values()];
  }

  /**
   * The HTML to send, each preview address replaced by its picture's
   * Content-ID, and the pictures to send with it. A picture that is neither
   * one of these nor public (https) is left out rather than sent broken.
   */
  outgoing(html: string): { html: string; images: InlineImage[] } {
    if (!/<img\b/i.test(html)) {
      return { html, images: [] };
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const images = new Map<string, InlineImage>();
    for (const img of Array.from(doc.querySelectorAll('img'))) {
      const src = (img.getAttribute('src') ?? '').trim();
      const picture = this.byUrl.get(src);
      if (picture) {
        images.set(picture.contentId, picture);
        img.setAttribute('src', `cid:${picture.contentId}`);
      } else if (!/^https:/i.test(src)) {
        img.remove();
      }
    }
    return { html: doc.body.innerHTML, images: [...images.values()] };
  }
}
